import { useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/Button';
import { Modal } from '../components/Modal';
import { Typography } from '../components/Typography';
import { cancelBookingErrorType, cancelBookingSuccessType, CancellationReason } from '../../../APIDocs/apiTypes';
import { RestTypes, useJbpQuery } from '../hooks/useJbpQuery';
import { useQueryClient } from '@tanstack/react-query';
import { FormProvider, useForm } from 'react-hook-form';
import * as Yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { FormButton } from '@portal_components/components/FormButton';
import { FormTextArea } from '@portal_components/components/FormTextArea';
import { FormSearchDropdown } from '@portal_components/components/FormSearchDropdown';
import { useUserInfo } from '../hooks/useUserInfo';
import { GenericLoadingElement } from '@/components/GenericLoadingElement';
import { selectCancellationReasons, selectStandingDataLoaded } from '@/reducers/standingDataSlice';
import { useAppSelector } from '@/store';

interface CancelJobProps {
  bookingId: string;
  mpan: string;
  serviceLine: string;
  closeForm: () => void;
}

type CancelJobFormValues = {
  reasonForCancellation: string;
  supplierReasonForCancellation?: string;
  customerReasonForCancellation?: string;
  cancellationComments: string;
};

enum ECancelJobModalType {
  CANCEL_JOB_MODAL,
  CANCEL_JOB_LOADING_MODAL,
  CANCEL_JOB_SUCCESS_MODAL,
  CANCEL_JOB_ERROR_MODAL
}

//To match party types by ID to their counterpart in the ODS SD_PARTY table
enum EPartyId {
  SIEMENS = 1,
  SUPPLIER = 2,
  CUSTOMER = 3
}

export const CancelJobForm = (props: CancelJobProps) => {
  const navigate = useNavigate();
  const cancellationReasons = useAppSelector(selectCancellationReasons);
  const standingDataLoaded = useAppSelector(selectStandingDataLoaded);
  const queryClient = useQueryClient();
  const { isInternalUser } = useUserInfo();

  const [formSubmitted, setFormSubmitted] = useState(false);

  const [currentModal, setCurrentModal] = useState<ECancelJobModalType>(ECancelJobModalType.CANCEL_JOB_MODAL);

  const validationSchema = Yup.object().shape({
    reasonForCancellation: isInternalUser ? Yup.string().required('Reason for cancellation is required') : Yup.string().optional(),
    supplierReasonForCancellation: Yup.string().when(['reasonForCancellation'], ([reasonForCancellation], schema) => {
      if (!isInternalUser || reasonForCancellation === 'Requested By Supplier') {
        return schema.required('Supplier reason for cancellation is required');
      }
      return schema.optional();
    }),
    customerReasonForCancellation: Yup.string().when(['reasonForCancellation'], ([reasonForCancellation], schema) => {
      if (reasonForCancellation === 'Requested By Customer') {
        return schema.required('Customer reason for cancellation is required');
      }
      return schema.optional();
    }),
    cancellationComments: Yup.string().required('Cancellation comments are required')
  });

  const submitJobCancellation = async () => {
    setFormSubmitted(true);
    setCurrentModal(ECancelJobModalType.CANCEL_JOB_LOADING_MODAL);
    await triggerSubmitJobCancellation();
  };

  const onSuccessfulJobCancellation = () => {
    setCurrentModal(ECancelJobModalType.CANCEL_JOB_SUCCESS_MODAL);
    queryClient.invalidateQueries({ queryKey: [`get-booking-list-${props.mpan}-${props.serviceLine}`] });
    setFormSubmitted(false);
  };

  const onJobCancellationError = () => {
    setCurrentModal(ECancelJobModalType.CANCEL_JOB_ERROR_MODAL);
    setFormSubmitted(false);
  };

  const handleJobSuccessfullyCancelledModal = () => {
    props.closeForm();
    navigate(`/site/${props.mpan}/${props.serviceLine}`);
  };

  const defaultValues = {
    reasonForCancellation: '',
    supplierReasonForCancellation: '',
    customerReasonForCancellation: '',
    cancellationComments: ''
  };

  const methods = useForm<CancelJobFormValues>({
    values: defaultValues,
    resolver: yupResolver<CancelJobFormValues>(validationSchema as any)
  });

  const [reasonForCancellation, supplierReasonForCancellation, customerReasonForCancellation, cancellationComments] = methods.watch([
    'reasonForCancellation',
    'supplierReasonForCancellation',
    'customerReasonForCancellation',
    'cancellationComments'
  ]);

  const [showSupplierReasonsDropdown, showCustomerReasonsDropdown] = useMemo(() => {
    const showSupplier = reasonForCancellation === 'Requested By Supplier';
    const showCustomer = reasonForCancellation === 'Requested By Customer';
    return [showSupplier, showCustomer];
  }, [reasonForCancellation]);

  const cancelBookingPayload = useCallback(() => {
    if (!isInternalUser) {
      return {
        jobCancellationReason: `${supplierReasonForCancellation} - ${cancellationComments}`,
        cancellationParty: 'supplier'
      };
    } else if (reasonForCancellation === 'Requested By Supplier') {
      return {
        jobCancellationReason: `${supplierReasonForCancellation} - ${cancellationComments}`,
        cancellationParty: 'supplier'
      };
    } else if (reasonForCancellation === 'Requested By Customer') {
      return {
        jobCancellationReason: `${customerReasonForCancellation} - ${cancellationComments}`,
        cancellationParty: 'customer'
      };
    } else {
      return {
        jobCancellationReason: `${reasonForCancellation} - ${cancellationComments}`,
        cancellationParty: 'siemens'
      };
    }
  }, [isInternalUser, reasonForCancellation, supplierReasonForCancellation, cancellationComments, customerReasonForCancellation]);

  const { refetch: triggerSubmitJobCancellation } = useJbpQuery<cancelBookingSuccessType, cancelBookingErrorType>(
    RestTypes.Delete,
    cancelBookingPayload(),
    [`${props.bookingId}-cancelJob`],
    `jbp/${props.mpan}/booking/${props.bookingId}`,
    false,
    props.serviceLine,
    onSuccessfulJobCancellation,
    onJobCancellationError
  );

  const modalTitle = 'Job Cancellation';

  const handleCancelButtonClick = () => {
    props.closeForm();
    methods.reset(defaultValues);
  };

  return (
    <>
      {currentModal === ECancelJobModalType.CANCEL_JOB_LOADING_MODAL && (
        <Modal title={modalTitle}>
          <Typography content={`Please wait whilst your job cancellation is processed`} className="mb-3 loading" />
          <span className="pl-10"></span>
        </Modal>
      )}
      {currentModal === ECancelJobModalType.CANCEL_JOB_MODAL && (
        <Modal title={modalTitle}>
          <div className="flex flex-col">
            {!standingDataLoaded ? (
              <GenericLoadingElement count={1} height="h-4" />
            ) : (
              cancellationReasons && (
                <FormProvider {...methods}>
                  <form onSubmit={!formSubmitted ? methods.handleSubmit(submitJobCancellation) : undefined}>
                    {isInternalUser && (
                      <FormSearchDropdown<CancelJobFormValues>
                        name="reasonForCancellation"
                        title="Reason for Cancellation"
                        mandatory
                        placeholder="Please select the Reason for Cancellation"
                        elementsForDisplay={(cancellationReasons ?? [])
                          .filter((c: CancellationReason) => c.partyId == EPartyId.SIEMENS)
                          .map((reason: CancellationReason) => ({
                            title: reason.cancellationReasonDescription,
                            value: reason.cancellationReasonDescription
                          }))}
                      />
                    )}
                    {(!isInternalUser || showSupplierReasonsDropdown) && (
                      <FormSearchDropdown<CancelJobFormValues>
                        name="supplierReasonForCancellation"
                        title="Supplier Reasons for Cancellation"
                        placeholder="Please select Supplier Reason for Cancellation"
                        elementsForDisplay={(cancellationReasons ?? [])
                          .filter((c: CancellationReason) => c.partyId == EPartyId.SUPPLIER)
                          .map((reason: CancellationReason) => ({
                            title: reason.cancellationReasonDescription,
                            value: reason.cancellationReasonDescription
                          }))}
                      />
                    )}
                    {showCustomerReasonsDropdown && (
                      <FormSearchDropdown<CancelJobFormValues>
                        name="customerReasonForCancellation"
                        title="Customer Reasons for Cancellation"
                        placeholder="Please select Customer Reason for Cancellation"
                        elementsForDisplay={(cancellationReasons ?? [])
                          .filter((c: CancellationReason) => c.partyId == EPartyId.CUSTOMER)
                          .map((reason: CancellationReason) => ({
                            title: reason.cancellationReasonDescription,
                            value: reason.cancellationReasonDescription
                          }))}
                      />
                    )}
                    <FormTextArea<CancelJobFormValues>
                      name="cancellationComments"
                      title="Additional Comments"
                      mandatory
                      placeholder="You must provide additional comments for this cancellation"
                      maxLength={450}
                    />
                    <Button primary={false} text="Cancel" className="mr-4 mt-5" disabled={false} onClick={handleCancelButtonClick} />
                    <FormButton type="submit" disabled={formSubmitted} primary icon>
                      Submit
                    </FormButton>
                  </form>
                </FormProvider>
              )
            )}
          </div>
        </Modal>
      )}
      {currentModal === ECancelJobModalType.CANCEL_JOB_SUCCESS_MODAL && (
        <Modal title={modalTitle}>
          <Typography content={`The Job has been cancelled successfully`} className="mb-3" />
          <Button primary={false} text="OK" className="mr-4 mt-5" disabled={false} onClick={handleJobSuccessfullyCancelledModal} />
        </Modal>
      )}
      {currentModal === ECancelJobModalType.CANCEL_JOB_ERROR_MODAL && (
        <Modal title={modalTitle}>
          <Typography content={`Something went wrong, please try again later`} className="mb-3" />
          <Button primary={false} text="OK" className="mr-4 mt-5" disabled={false} onClick={() => props.closeForm()} />
        </Modal>
      )}
    </>
  );
};
